import React, { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogTitle, Button, DialogContent } from "@mui/material";
import FieldSection, { Field } from "../../general/FieldSection";
import dayjs from "dayjs";
import { UpdateWorkshopInstanceDto, WorkshopDto, WorkshopInstanceDto } from "../../../types/workshopTypes";
import { MembersFieldDto } from "../../../types/membersTypes";

interface WorkshopInstanceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (workshopInstance: UpdateWorkshopInstanceDto) => void;
  workshop: WorkshopDto;
  workshopInstance: WorkshopInstanceDto;
  members: MembersFieldDto[];
}

/**
 * Displays a dialog to add or edit a workshop instance.
 * @param open Whether the dialog should be open.
 * @param onClose Function to close the dialog.
 * @param onSave Function to save the workshop instance.
 * @param workshop The workshop to add or edit.
 * @param workshopInstance The workshop instance to add or edit.
 * @param members The members to add or edit.
 * @returns A dialog to add or edit a workshop instance.
 */
const WorkshopInstanceDialog: React.FunctionComponent<WorkshopInstanceDialogProps> = ({
  open,
  onClose,
  onSave,
  workshop,
  workshopInstance,
  members,
}: WorkshopInstanceDialogProps) => {
  const [formData, setFormData] = useState<UpdateWorkshopInstanceDto>({
    date: workshopInstance.date,
    startTime: workshopInstance.startTime || undefined,
    endTime: workshopInstance.endTime || undefined,
    location: workshopInstance.location || undefined,
    targetGroup: workshopInstance.targetGroup || undefined,
    maximumParticipants: workshopInstance.maximumParticipants || undefined,
    referents: workshopInstance.referents || [],
  });

  // Error states for the form
  const [errordate, setErrorDate] = useState<boolean>(false);
  const [errorStartTime, setErrorStartTime] = useState<boolean>(false);
  const [errorEndTime, setErrorEndTime] = useState<boolean>(false);

  const onChangeDate = (value: unknown) => {
    setFormData({ ...formData, date: value as Date });
  };

  const onChangeStartTime = (newStartTime: unknown) => {
    setErrorStartTime(false);
    setErrorEndTime(false);
    if (!newStartTime) {
      setErrorStartTime(true);
    } else if (!formData.endTime) {
      setErrorEndTime(true);
    } else if (
      formData.endTime ? new Date(formData.endTime).getTime() < new Date(newStartTime as Date).getTime() : null
    ) {
      setErrorEndTime(true);
    } else {
      setErrorStartTime(false);
    }
    setFormData({ ...formData, startTime: newStartTime as Date });
  };

  const onChangeEndTime = (newEndTime: unknown) => {
    setErrorEndTime(false);
    setErrorStartTime(false);
    if (!newEndTime) {
      setErrorEndTime(true);
    } else if (!formData.startTime) {
      setErrorStartTime(true);
    } else if (
      formData.startTime ? new Date(formData.startTime).getTime() > new Date(newEndTime as Date).getTime() : null
    ) {
      setErrorEndTime(true);
    } else {
      setErrorEndTime(false);
    }
    setFormData({ ...formData, endTime: newEndTime as Date });
  };

  const onChangeLocation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, location: event.target.value });
  };

  const onChangeTargetGroup = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, targetGroup: event.target.value });
  };

  const onChangeInternalInstructors = (_event: React.ChangeEvent<object>, value: MembersFieldDto[]) => {
    setFormData({ ...formData, referents: value });
  };

  const onChangeMaxParticipants = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, maximumParticipants: Number(event.target.value) });
  };

  // Function to reset the form
  const resetForm = () => {
    setErrorDate(false);
    setErrorStartTime(false);
    setErrorEndTime(false);
    setFormData({
      date: workshopInstance.date,
      startTime: workshopInstance.startTime || undefined,
      endTime: workshopInstance.endTime || undefined,
      location: workshopInstance.location || undefined,
      targetGroup: workshopInstance.targetGroup || undefined,
      maximumParticipants: workshopInstance.maximumParticipants || undefined,
      referents: workshopInstance.referents || [],
    });
  };

  // Function to handle the close button
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Function to handle the save button
  const handleSave = () => {
    setErrorDate(false);
    if (!formData.date || !dayjs(formData.date).isValid()) {
      setErrorDate(true);
      return;
    } else if (errorStartTime || errorEndTime) {
      return;
    }
    onSave(formData);
    resetForm();
    handleClose();
  };

  // Update form data when workshopInstance prop changes e.g. after saving
  useEffect(() => {
    setFormData({
      date: workshopInstance.date,
      startTime: workshopInstance.startTime || undefined,
      endTime: workshopInstance.endTime || undefined,
      location: workshopInstance.location || undefined,
      targetGroup: workshopInstance.targetGroup || undefined,
      maximumParticipants: workshopInstance.maximumParticipants || undefined,
      referents: workshopInstance.referents || [],
    });
  }, [workshopInstance]);

  const dialogFields: Array<Field> = [
    {
      label: "Datum",
      type: "Date",
      state: dayjs(formData.date),
      width: "half",
      onChangeCallback: onChangeDate,
      error: errordate,
      helperText: errordate ? "Bitte lege ein gültiges Datum fest" : "",
    },
    {
      label: "Startzeit",
      type: "Time",
      state: dayjs(formData.startTime),
      width: "half",
      onChangeCallback: onChangeStartTime,
      helperText: errorStartTime ? "Bitte gib eine gültige Startzeit an" : "",
      error: errorStartTime,
    },
    {
      label: "Endzeit",
      type: "Time",
      state: dayjs(formData.endTime),
      width: "half",
      onChangeCallback: onChangeEndTime,
      helperText: errorEndTime ? "Bitte gib eine gültige Endzeit an" : "",
      error: errorEndTime,
    },
    {
      label: "Ort",
      type: "Text",
      state: formData.location,
      width: "half",
      onChangeCallback: onChangeLocation,
    },
    {
      label: "Zielgruppe",
      type: "Text",
      state: formData.targetGroup,
      width: "half",
      onChangeCallback: onChangeTargetGroup,
    },
    {
      label: "Max. Teilnehmer",
      type: "Text",
      inputType: "number",
      state: formData.maximumParticipants,
      width: "half",
      onChangeCallback: onChangeMaxParticipants,
    },
    {
      label: "Referenten",
      type: "MembersAutocomplete",
      state: formData.referents,
      width: "full",
      onChangeCallback: onChangeInternalInstructors,
      options: members,
      value: formData.referents || [],
    },
  ];

  return (
    <Dialog open={open}>
      <DialogTitle>{workshop.workshopName}</DialogTitle>
      <DialogContent>
        <FieldSection fields={dialogFields} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Abbrechen</Button>
        <Button onClick={handleSave} disabled={errorStartTime || errorEndTime}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkshopInstanceDialog;
